import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Camera, CalendarDays, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatTime(time) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

export default function Rebook() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const preselect = Number(searchParams.get('slot'));

  const [offer, setOffer] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios
      .get(`${API}/public/rebook/${token}`)
      .then(({ data }) => setOffer(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const chooseSlot = async (slotIndex) => {
    setError('');
    setBooking(true);
    try {
      const { data } = await axios.post(`${API}/public/rebook/${token}`, { slot: slotIndex });
      setBooked({ date: data.date, time: data.time });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to book. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-6">
          <Camera className="w-8 h-8 text-background" />
        </div>
        {children}
      </div>
    </div>
  );

  if (loading) return shell(<p className="text-text-secondary">Loading...</p>);

  if (notFound) {
    return shell(
      <div data-testid="rebook-invalid">
        <XCircle className="w-10 h-10 text-alert mx-auto mb-4" />
        <h1 className="text-2xl font-serif font-bold text-foreground mb-2">Link Not Found</h1>
        <p className="text-text-secondary">This reschedule link is invalid or has expired.</p>
      </div>
    );
  }

  if (booked || offer.status === 'rescheduled') {
    const slot = booked || offer.rebooked_slot || {};
    return shell(
      <div data-testid="rebook-success">
        <CheckCircle className="w-10 h-10 text-success mx-auto mb-4" />
        <h1 className="text-2xl font-serif font-bold text-foreground mb-2">You're Booked!</h1>
        <p className="text-text-secondary">
          Your {offer.session_type || 'session'} is confirmed for{' '}
          <span className="text-foreground font-medium">
            {slot.date}
            {slot.time && ` at ${formatTime(slot.time)}`}
          </span>
          . A confirmation email is on its way.
        </p>
      </div>
    );
  }

  if (offer.status !== 'reschedule_offered') {
    const expired = offer.status === 'expired';
    return shell(
      <div data-testid={expired ? 'rebook-expired' : 'rebook-unavailable'}>
        <XCircle className="w-10 h-10 text-alert mx-auto mb-4" />
        <h1 className="text-2xl font-serif font-bold text-foreground mb-2">
          {expired ? 'Offer Expired' : 'Offer No Longer Available'}
        </h1>
        <p className="text-text-secondary">
          {expired
            ? 'These dates are no longer available. Please submit a new booking request or reach out directly.'
            : 'This reschedule offer has already been used or withdrawn.'}
        </p>
      </div>
    );
  }

  return shell(
    <div data-testid="rebook-offer">
      <h1 className="text-2xl font-serif font-bold text-foreground mb-2">Pick a New Date</h1>
      <p className="text-text-secondary mb-6">
        Hi {offer.name.split(' ')[0]} — {offer.original_date} didn't work out, but these dates are available for your{' '}
        {offer.session_type || 'session'}. Tap one to book it instantly.
      </p>
      <div className="space-y-3">
        {offer.suggested_dates.map((slot) => (
          <button
            key={slot.slot}
            onClick={() => chooseSlot(slot.slot)}
            disabled={booking}
            className={`w-full flex items-center gap-3 bg-surface border rounded-xl p-4 text-left transition-all hover:border-primary disabled:opacity-50 ${
              preselect === slot.slot ? 'border-primary ring-1 ring-primary' : 'border-border'
            }`}
            data-testid={`rebook-slot-${slot.slot}`}
          >
            <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
              <CalendarDays className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="font-medium text-foreground">{slot.date}</div>
              {slot.time && <div className="text-sm text-text-secondary">{formatTime(slot.time)}</div>}
            </div>
          </button>
        ))}
      </div>
      {error && (
        <div className="mt-4 bg-alert/10 border border-alert/20 text-alert rounded-lg p-3 text-sm" data-testid="rebook-error">
          {error}
        </div>
      )}
    </div>
  );
}
